import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Badge, Button, Card } from '@vnetwork/ui/primitives';
import { ConfirmDialog } from '@vnetwork/ui/feedback';
import {
  useResolveNegotiation,
  useRejectNegotiation,
  useConvertNegotiationToContract,
  type JourneyNegotiation,
} from '@/api/customer-journey';
import { formatDateTime, formatMoney } from '@/lib/format';
import {
  negotiationTone,
  negotiationStatusLabel,
  canResolveNegotiation,
  canRejectNegotiation,
  canConvertNegotiation,
} from './sale-process-helpers';
import { NegotiationHistoryPanel } from './negotiation-history-panel';
import { EntityLink } from '@/components/entity-link';

/**
 * One negotiation in the deal-negotiation list. Shows status/value/linked
 * contract, expands to its history, and (when its status allows) exposes
 * Resolve / Reject / Convert-to-contract. Gating mirrors the legacy client:
 * Resolve/Reject on the in-flight statuses, Convert only once completed.
 * Each row owns its own per-negotiation mutation hooks so hook order stays
 * stable across the list.
 */

type Pending = 'resolve' | 'reject' | 'convert' | null;

export function NegotiationRow({
  negotiation,
  expanded,
  onToggle,
  onToast,
}: {
  negotiation: JourneyNegotiation;
  expanded: boolean;
  onToggle: () => void;
  onToast: (ok: boolean, message: string, detail?: string) => void;
}) {
  const id = negotiation.contract_negotiation_id ?? 0;
  const resolve = useResolveNegotiation(id);
  const reject = useRejectNegotiation(id);
  const convert = useConvertNegotiationToContract(id);

  const status = negotiation.contract_negotiation_status;
  const showResolve = canResolveNegotiation(negotiation);
  const showReject = canRejectNegotiation(negotiation);
  const showConvert = canConvertNegotiation(negotiation);
  const contractId = negotiation.contract_id ?? undefined;
  const value = negotiation.contract_negotiation_value;

  const run =
    (kind: Exclude<Pending, null>) =>
    () => {
      const map = {
        resolve: {
          m: resolve,
          ok: 'Negotiation resolved',
          err: 'resolve negotiation',
          payload: { resolution_notes: 'Resolved' },
        },
        reject: {
          m: reject,
          ok: 'Negotiation rejected',
          err: 'reject negotiation',
          payload: { reason: 'Rejected' },
        },
        convert: {
          m: convert,
          ok: 'Converted to contract',
          err: 'convert to contract',
          payload: { notes: 'Converted to contract' },
        },
      }[kind];
      map.m.mutate(map.payload as never, {
        onSuccess: () => onToast(true, map.ok),
        onError: (err: unknown) =>
          onToast(false, `Failed to ${map.err}`, err instanceof Error ? err.message : undefined),
      });
    };

  return (
    <Card flat style={{ overflow: 'hidden' }}>
      <Button
        type="button"
        variant="ghost"
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--vn-space-3)',
          padding: 'var(--vn-space-3)',
          cursor: 'pointer',
          textAlign: 'left',
          height: 'auto',
          justifyContent: 'flex-start',
          borderRadius: 0,
        }}
      >
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <Badge tone={negotiationTone(status)} appearance="subtle" size="sm">
          {negotiationStatusLabel(status)}
        </Badge>
        {value ? (
          <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-semibold)' }}>
            {formatMoney(value)}
          </span>
        ) : null}
        <span style={{ marginLeft: 'auto', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>
          {formatDateTime(negotiation.updated_at || negotiation.created_at)}
        </span>
      </Button>

      {contractId ? (
        <div style={{ padding: '0 var(--vn-space-3) var(--vn-space-2)', display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', fontSize: 'var(--vn-font-size-sm)' }}>
          <ExternalLink size={14} style={{ color: 'var(--vn-fg-muted)', flexShrink: 0 }} />
          <EntityLink to={`/contracts/${contractId}`} id={contractId} />
        </div>
      ) : null}

      {expanded ? (
        <div
          style={{
            padding: 'var(--vn-space-3)',
            borderTop: '1px solid var(--vn-border-default)',
            display: 'grid',
            gap: 'var(--vn-space-3)',
          }}
        >
          <NegotiationHistoryPanel negotiationId={id} />

          <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
            {showResolve ? (
              <Button size="sm" variant="primary" onClick={run('resolve')} loading={resolve.isPending}>
                Resolve
              </Button>
            ) : null}
            {showReject ? (
              <Button size="sm" variant="secondary" onClick={run('reject')} loading={reject.isPending}>
                Reject
              </Button>
            ) : null}
            {showConvert && !contractId ? (
              <ConvertButton pending={convert.isPending} onConfirm={run('convert')} />
            ) : null}
          </div>
        </div>
      ) : null}
    </Card>
  );
}

/** Convert-to-contract with a confirm guard — this is the journey→contract step. */
function ConvertButton({ pending, onConfirm }: { pending: boolean; onConfirm: () => void }) {
  return (
    <ConfirmGuard
      trigger={(open) => (
        <Button size="sm" variant="primary" onClick={open} loading={pending}>
          Convert to contract
        </Button>
      )}
      onConfirm={onConfirm}
      pending={pending}
    />
  );
}

/** Small local confirm wrapper to keep the button co-located with its dialog. */
function ConfirmGuard({
  trigger,
  onConfirm,
  pending,
}: {
  trigger: (open: () => void) => ReactNode;
  onConfirm: () => void;
  pending: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {trigger(() => setOpen(true))}
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Convert to contract"
        description="Create a contract from this completed negotiation. The journey advances to the contract stage."
        confirmLabel="Convert"
        loading={pending}
        onConfirm={() => {
          onConfirm();
          setOpen(false);
        }}
      />
    </>
  );
}

export default NegotiationRow;
